import {
    PrimaryRecordResource,
    PrimaryRecordResourceCategory,
    PrimaryRecordOrOneUpResource,
    Coding,
} from './MedicalDataTypes';
import { CareTeamMember, ContactDetails, ResourceRecord, Note, WithTimestamps } from './DynamoTypes';
import { AudioSectionArtifact, OCRSectionArtifact, PageDescriptionArtifact } from './MedicalProfileFiles';

export type ClusterHealthStatus = 'green' | 'yellow' | 'red';

export type SearchTransformedResourceRecord = {
    primaryRecordResource: PrimaryRecordResource;
    medicalProfileId: string;
    literalIdentifiers: string[];
    flexibleIdentifiers: string[];
    isPrimaryScore: number;
};

type ResourceGroupingProperties = {
    literalIdentifiers?: string[];
    flexibleIdentifiers?: string[];
    isPrimaryScore: number;
    groupId?: string;
    isPrimary?: boolean;
    isUnique: boolean;
};

type ResourceCommonProperties = {
    id: string;
    medicalProfileId: string;
    documentVector: number[];
    notes?: string | null;
};

type StartAndEndDateTime = {
    startDateTime?: string;
    endDateTime?: string;
};

// For resources that aren't grouped, isUnique and isPrimary are
// set to true by default, otherwise cross-index queries are difficult
type UngroupedResourceProperties = {
    isPrimary: true;
    isUnique: true;
};

export type ConditionDocument = ResourceCommonProperties &
    StartAndEndDateTime &
    ResourceGroupingProperties & {
        name: string;
        status: string;
    };

export type AllergyDocument = ResourceCommonProperties &
    StartAndEndDateTime &
    ResourceGroupingProperties & {
        name: string;
        status: string;
        allergyType?: string | null;
    };

export type MedicationDocument = ResourceCommonProperties &
    StartAndEndDateTime &
    ResourceGroupingProperties & {
        name: string;
        fdaName?: string | null;
        genericNameOrActiveIngredient?: string | null;
        strength?: string | null;
        form?: string | null;
        route?: string | null;
        quantity?: string | null;
        asNeeded?: boolean | null;
        frequency?: string | null;
        isActive: boolean;
        whenToTake?: string | null;
        numberOfRefills?: string | null;
        lastRefillDateTime?: string | null;
        providerCareTeamMemberId?: string | null;
        providerName?: string | null;
    };

export type VisitDocument = ResourceCommonProperties &
    StartAndEndDateTime & {
        visitType?: string | null;
        reason?: string | null;
        duration?: number | null;
        providerCareTeamMemberId?: string | null;
        providerName?: string | null;
        address1?: string | null;
        address2?: string | null;
        city?: string | null;
        state?: string | null;
        zipCode?: string | null;
        clinicalImpressions?: string[] | null;
        // Visits aren't truly grouped, so no grouping properties; however,
        // we still need to include the `isPrimary` property otherwise cross-index queries are difficult
    } & UngroupedResourceProperties;

export type VaccinationDocument = ResourceCommonProperties &
    ResourceGroupingProperties & {
        vaccineType: Coding;
        dateTime?: string | null;
        address1?: string | null;
        address2?: string | null;
        city?: string | null;
        state?: string | null;
        zipCode?: string | null;
    };

export type ProcedureDocument = ResourceCommonProperties &
    ResourceGroupingProperties & {
        name: string;
        dateTime?: string | null;
        providerCareTeamMemberId?: string | null;
        providerName?: string | null;
        address1?: string | null;
        address2?: string | null;
        city?: string | null;
        state?: string | null;
        zipCode?: string | null;
        results?: string | null;
    };

export type ImagingDocument = ResourceCommonProperties & {
    name: string;
    dateTime?: string | null;
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
    results?: string | null;
} & UngroupedResourceProperties;

export type LabDocument = Omit<ResourceCommonProperties, 'notes'> & {
    labType: Coding;
    dateTime?: string | null;
    values: {
        componentCoding: Coding;
    }[];
} & ResourceGroupingProperties;

export type OCRSectionDocument = UngroupedResourceProperties & {
    id: string;
    medicalProfileId: string;
    medicalProfileFileId: string;
    page: number;
    sectionIndex: number;
    text: string;
    documentVector: number[];
};

export type AudioSectionDocument = UngroupedResourceProperties & {
    id: string;
    medicalProfileId: string;
    medicalProfileFileId: string;
    page: number;
    text: string;
    documentVector: number[];
    startTime: number;
    endTime: number;
    speakerIndex: number;
};

export type PageDescriptionDocument = UngroupedResourceProperties & {
    id: string;
    medicalProfileId: string;
    medicalProfileFileId: string;
    page: number;
    text: string;
    documentVector: number[];
};

export type ContactDetailsSubDocument = {
    firstName?: string | null;
    lastName?: string | null;
    company?: string | null;
    preferredFirstName?: string | null;
    dob?: string | null;
    sex?: string | null;
    gender?: string | null;
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
    relationship?: string | null;
    description?: string | null;
    faxNumber?: string | null;
    url?: string | null;
    role?: string | null;
};

export type CareTeamMemberDocument = UngroupedResourceProperties & {
    id: string;
    medicalProfileId: string;
    memberType: string;
    documentVector: number[];
    metadata: {
        isActive: boolean;
        supportCareTeamMemberType?: string | null;
        providerType?: string;
        providerSpecialty?: string | null;
        insuranceProviderName?: string;
        primaryCarrierFirstName?: string;
        primaryCarrierLastName?: string;
        insuranceRole?: string;
        groupNumber?: string;
        planNumber?: string;
        memberId?: string;
        expirationDate?: string;
        isPreferred?: boolean;
    };
    contactDetails?: ContactDetailsSubDocument;
};

export type NoteDocument = UngroupedResourceProperties & {
    id: string;
    medicalProfileId: string;
    entityType: string;
    entityId: string;
    title: string;
    content: string;
    tags: string[];
    createUserId: string;
    createDateTime: string | null;
    modifiedUserId?: string | null;
    isSystemGenerated: boolean;
    documentVector: number[];
};

export type Target =
    | PrimaryRecordResourceCategory<PrimaryRecordOrOneUpResource>
    | 'ocr-section'
    | 'audio-section'
    | 'page-description'
    | 'care-team-member'
    | 'note';

type TargetDocumentMap = {
    condition: ConditionDocument;
    allergy: AllergyDocument;
    medication: MedicationDocument;
    visit: VisitDocument;
    'ocr-section': OCRSectionDocument;
    'audio-section': AudioSectionDocument;
    'page-description': PageDescriptionDocument;
    vaccination: VaccinationDocument;
    procedure: ProcedureDocument;
    imaging: ImagingDocument;
    lab: LabDocument;
    'care-team-member': CareTeamMemberDocument;
    note: NoteDocument;
};

type BaseDocumentOperation<T extends Target> = {
    target: T;
    document: TargetDocumentMap[T];
    id: string;
    // For messages that land in the FIFO queue, order is guaranteed only within a message group.
    // We don't care about message order across different medical profiles,
    // but we do care about order within a single medical profile,
    // so generally we will use the medical profile ID as the message group ID
    groupId: string;
};

// An upsert operation creates a new document if it doesn't exist
// or updates a document if it does exist
export type BaseUpsertOperation<T extends Target> = BaseDocumentOperation<T> & {
    action: 'upsert';
};
export type ConditionUpsertOperation = BaseUpsertOperation<'condition'>;
export type AllergyUpsertOperation = BaseUpsertOperation<'allergy'>;
export type MedicationUpsertOperation = BaseUpsertOperation<'medication'>;
export type VisitUpsertOperation = BaseUpsertOperation<'visit'>;
export type VaccinationUpsertOperation = BaseUpsertOperation<'vaccination'>;
export type ProcedureUpsertOperation = BaseUpsertOperation<'procedure'>;
export type ImagingUpsertOperation = BaseUpsertOperation<'imaging'>;
export type LabUpsertOperation = BaseUpsertOperation<'lab'>;
export type OCRSectionUpsertOperation = BaseUpsertOperation<'ocr-section'>;
export type AudioSectionUpsertOperation = BaseUpsertOperation<'audio-section'>;
export type PageDescriptionUpsertOperation = BaseUpsertOperation<'page-description'>;
export type CareTeamMemberUpsertOperation = BaseUpsertOperation<'care-team-member'>;
export type NoteUpsertOperation = BaseUpsertOperation<'note'>;
export type UpsertOperation =
    | ConditionUpsertOperation
    | AllergyUpsertOperation
    | MedicationUpsertOperation
    | VisitUpsertOperation
    | OCRSectionUpsertOperation
    | AudioSectionUpsertOperation
    | PageDescriptionUpsertOperation
    | VaccinationUpsertOperation
    | ProcedureUpsertOperation
    | ImagingUpsertOperation
    | LabUpsertOperation
    | CareTeamMemberUpsertOperation
    | NoteUpsertOperation;

// An index operation creates a new document if it doesn't exist
// or overwrites a document if it does exist
export type BaseIndexOperation<T extends Target> = BaseDocumentOperation<T> & {
    action: 'index';
};
export type ConditionIndexOperation = BaseIndexOperation<'condition'>;
export type AllergyIndexOperation = BaseIndexOperation<'allergy'>;
export type MedicationIndexOperation = BaseIndexOperation<'medication'>;
export type VisitIndexOperation = BaseIndexOperation<'visit'>;
export type VaccinationIndexOperation = BaseIndexOperation<'vaccination'>;
export type ProcedureIndexOperation = BaseIndexOperation<'procedure'>;
export type ImagingIndexOperation = BaseIndexOperation<'imaging'>;
export type LabIndexOperation = BaseIndexOperation<'lab'>;
export type OCRSectionIndexOperation = BaseIndexOperation<'ocr-section'>;
export type AudioSectionIndexOperation = BaseIndexOperation<'audio-section'>;
export type PageDescriptionIndexOperation = BaseIndexOperation<'page-description'>;
export type CareTeamMemberIndexOperation = BaseIndexOperation<'care-team-member'>;
export type NoteIndexOperation = BaseIndexOperation<'note'>;
export type IndexOperation =
    | ConditionIndexOperation
    | AllergyIndexOperation
    | MedicationIndexOperation
    | VisitIndexOperation
    | OCRSectionIndexOperation
    | AudioSectionIndexOperation
    | PageDescriptionIndexOperation
    | VaccinationIndexOperation
    | ProcedureIndexOperation
    | ImagingIndexOperation
    | LabIndexOperation
    | CareTeamMemberIndexOperation
    | NoteIndexOperation;

// Delete operations do not contain a document, only the id and the target
export type BaseDeleteOperation<T extends Target> = Omit<BaseDocumentOperation<T>, 'document'> & {
    action: 'delete';
};
export type ConditionDeleteOperation = BaseDeleteOperation<'condition'>;
export type AllergyDeleteOperation = BaseDeleteOperation<'allergy'>;
export type MedicationDeleteOperation = BaseDeleteOperation<'medication'>;
export type VisitDeleteOperation = BaseDeleteOperation<'visit'>;
export type VaccinationDeleteOperation = BaseDeleteOperation<'vaccination'>;
export type ProcedureDeleteOperation = BaseDeleteOperation<'procedure'>;
export type ImagingDeleteOperation = BaseDeleteOperation<'imaging'>;
export type LabDeleteOperation = BaseDeleteOperation<'lab'>;
export type OCRSectionDeleteOperation = BaseDeleteOperation<'ocr-section'>;
export type AudioSectionDeleteOperation = BaseDeleteOperation<'audio-section'>;
export type PageDescriptionDeleteOperation = BaseDeleteOperation<'page-description'>;
export type CareTeamMemberDeleteOperation = BaseDeleteOperation<'care-team-member'>;
export type NoteDeleteOperation = BaseDeleteOperation<'note'>;
export type DeleteOperation =
    | ConditionDeleteOperation
    | AllergyDeleteOperation
    | MedicationDeleteOperation
    | VisitDeleteOperation
    | OCRSectionDeleteOperation
    | AudioSectionDeleteOperation
    | PageDescriptionDeleteOperation
    | VaccinationDeleteOperation
    | ProcedureDeleteOperation
    | ImagingDeleteOperation
    | LabDeleteOperation
    | CareTeamMemberDeleteOperation
    | NoteDeleteOperation;

export type Operation = UpsertOperation | IndexOperation | DeleteOperation;

export type State =
    | 'normal'
    | 'exporting'
    | 'backfilling'
    | 'runningPendingUpdates'
    | 'validatingPendingUpdates'
    | 'pendingDeletion';

export type IndexState = {
    // It's possible for `current` to be undefined and `new` to be defined
    // for a backfill that is populating a completely new target
    current?: string;
    // `new` will be undefined in the 'normal' state
    new?: string;
};

export type DynamoSource = 'resourceRecords' | 'medicalProfileFileArtifacts' | 'careTeamMembers' | 'notes';

export type SupportedMedicalProfileFileArtifacts = OCRSectionArtifact | AudioSectionArtifact | PageDescriptionArtifact;

export type CareTeamMemberWithContactDetails = CareTeamMember & { contactDetails?: ContactDetails };

/**
 * Notes at the user level (without a medical profile id) are not supported in search
 */
export type NoteWithRequiredMedicalProfileId = WithTimestamps<Note> & { medicalProfileId: string };

export type SearchIndexIndexEvent<TSource extends DynamoSource, TRecord> = {
    source: TSource;
    action: 'index';
    record: TRecord;
};

export type SearchIndexDeleteEvent<TSource extends DynamoSource, TKey> = {
    source: TSource;
    action: 'delete';
    recordKey: TKey;
};

export type SearchIndexIndexEventSourceMap = {
    resourceRecords: ResourceRecord;
    medicalProfileFileArtifacts: SupportedMedicalProfileFileArtifacts;
    careTeamMembers: CareTeamMemberWithContactDetails;
    notes: NoteWithRequiredMedicalProfileId;
};

export type SearchIndexDeleteEventSourceMap = {
    resourceRecords: {
        resourceId: string;
        medicalProfileId: string;
        resourceCategory: PrimaryRecordResourceCategory;
    };
    medicalProfileFileArtifacts: {
        medicalProfileFileArtifactId: string;
        medicalProfileFileId: string;
        medicalProfileId: string;
        artifactType: SupportedMedicalProfileFileArtifacts['artifactType'];
    };
    careTeamMembers: {
        careTeamMemberId: string;
        medicalProfileId: string;
    };
    notes: {
        noteId: string;
        medicalProfileId: string;
    };
};

export type BaseSearchIndexEvent<TSource extends DynamoSource> =
    | SearchIndexIndexEvent<TSource, SearchIndexIndexEventSourceMap[TSource]>
    | SearchIndexDeleteEvent<TSource, SearchIndexDeleteEventSourceMap[TSource]>;

export type ResourceRecordSearchIndexIndexEvent = SearchIndexIndexEvent<'resourceRecords', ResourceRecord>;
export type ResourceRecordSearchIndexDeleteEvent = SearchIndexDeleteEvent<
    'resourceRecords',
    SearchIndexDeleteEventSourceMap['resourceRecords']
>;
export type ResourceRecordSearchIndexEvent = BaseSearchIndexEvent<'resourceRecords'>;

export type MedicalProfileFileArtifactSearchIndexIndexEvent = SearchIndexIndexEvent<
    'medicalProfileFileArtifacts',
    SupportedMedicalProfileFileArtifacts
>;
export type MedicalProfileFileArtifactSearchIndexDeleteEvent = SearchIndexDeleteEvent<
    'medicalProfileFileArtifacts',
    SearchIndexDeleteEventSourceMap['medicalProfileFileArtifacts']
>;
export type MedicalProfileFileArtifactSearchIndexEvent = BaseSearchIndexEvent<'medicalProfileFileArtifacts'>;

export type CareTeamMemberSearchIndexIndexEvent = SearchIndexIndexEvent<
    'careTeamMembers',
    CareTeamMemberWithContactDetails
>;
export type CareTeamMemberSearchIndexDeleteEvent = SearchIndexDeleteEvent<
    'careTeamMembers',
    SearchIndexDeleteEventSourceMap['careTeamMembers']
>;
export type CareTeamMemberSearchIndexEvent = BaseSearchIndexEvent<'careTeamMembers'>;

export type NoteSearchIndexIndexEvent = SearchIndexIndexEvent<'notes', NoteWithRequiredMedicalProfileId>;
export type NoteSearchIndexDeleteEvent = SearchIndexDeleteEvent<'notes', SearchIndexDeleteEventSourceMap['notes']>;
export type NoteSearchIndexEvent = BaseSearchIndexEvent<'notes'>;

export type SearchIndexEvent =
    | ResourceRecordSearchIndexEvent
    | MedicalProfileFileArtifactSearchIndexEvent
    | CareTeamMemberSearchIndexEvent
    | NoteSearchIndexEvent;
